import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Input, Modal, message } from 'antd';
import {
  simpleFetch,
  simplePost,
  simplePut,
  makeApiRequestAndHandleError,
} from 'common/utils/http';
import ReloadAllAttributesContext from '../context';

const getUrlByType = type => {
  let url = '/api/v1';
  switch (type) {
  case 'Genre':
    url += '/genres';
    break;
  case 'Section':
    url += '/sections';
    break;
  default:
  }

  return url;
};

const handleGenreSubmit = async data => {
  const url = getUrlByType('Genre');
  try {
    if (data.id !== undefined) {
      await simplePut(`${url}/${data.id}`, data);
    } else {
      await simplePost(url, data);
    }
  } catch {
    message.error('Something went wrong. Please try again later');
    return;
  }
  message.success('Action Success!');
};

const handleSectionSubmit = async (data, genre) => {
  const genreUrl = getUrlByType('Genre');
  const sectionUrl = getUrlByType('Section');

  makeApiRequestAndHandleError(async () => {
    const genreWithId = await simpleFetch(`${genreUrl}/${genre.name}`);
    if (data.id !== undefined) {
      await simplePut(`${sectionUrl}/${data.id}`, { ...data, genre_id: genreWithId.id });
    } else {
      await simplePost(sectionUrl, { ...data, genre_id: genreWithId.id });
    }
  });
  message.success('Action Success!');
};

const AddOrEditItemForm = ({ values, setValues, parentItem }) => {
  const { name, description, sort_order: sortOrder } = values;

  return (
    <div>
      <Form layout="vertical">
        <Form.Item label="Name">
          <Input onChange={e => setValues({ ...values, name: e.target.value })} value={name} />
        </Form.Item>
        <Form.Item label="Description">
          <Input
            onChange={e => setValues({ ...values, description: e.target.value })}
            value={description}
          />
        </Form.Item>
        <Form.Item label="Sort Order">
          <Input
            onChange={e => setValues({ ...values, sort_order: Number(e.target.value) })}
            type="number"
            value={sortOrder}
          />
        </Form.Item>
        {parentItem && (
          <Form.Item label="Belongs to Genre">
            <Input disabled value={parentItem.description} />
          </Form.Item>
        )}
      </Form>
    </div>
  );
};
AddOrEditItemForm.propTypes = {
  values: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    sort_order: PropTypes.number,
  }).isRequired,
  setValues: PropTypes.func.isRequired,
  parentItem: PropTypes.shape({ description: PropTypes.string.isRequired }),
};
AddOrEditItemForm.defaultProps = { parentItem: null };

const AddOrEditItemModal = ({ data, isOpen, type, parentItem, onClose }) => {
  const [values, setValues] = useState({});
  const { reload } = useContext(ReloadAllAttributesContext);
  const isEdit = data.name !== undefined;
  const reqUrl = getUrlByType(type);

  useEffect(() => {
    setValues({});
    if (isOpen && data.name) {
      makeApiRequestAndHandleError(async () => {
        simpleFetch(`${reqUrl}/${data.name}`).then(resp => setValues(resp));
      });
    }
  }, [data, isOpen, reqUrl]);

  const onSubmit = async () => {
    if (parentItem) {
      await handleSectionSubmit(values, parentItem);
    } else {
      await handleGenreSubmit(values);
    }
    reload();
    onClose();
  };

  return (
    <Modal
      footer={[
        <Button key="cancel" onClick={onClose} type="link">
          Cancel
        </Button>,
        <Button key="submit" onClick={onSubmit} type="primary">
          Save
        </Button>,
      ]}
      onCancel={onClose}
      open={isOpen}
      title={`${isEdit ? 'Edit' : 'Add'} ${type}`}
    >
      <AddOrEditItemForm parentItem={parentItem} setValues={setValues} values={values} />
    </Modal>
  );
};
AddOrEditItemModal.propTypes = {
  data: PropTypes.shape({ name: PropTypes.string, description: PropTypes.string }),
  isOpen: PropTypes.bool,
  type: PropTypes.string.isRequired,
  parentItem: PropTypes.shape({ description: PropTypes.string.isRequired }),
  onClose: PropTypes.func.isRequired,
};
AddOrEditItemModal.defaultProps = { data: {}, isOpen: false, parentItem: null };

export default AddOrEditItemModal;
